import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { WorkspaceCharactersTab, WorkspaceLocationsTab } from '@/components/WorkspaceTabs';
import { GettingStarted } from '@/components/GettingStarted';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Plus, FolderOpen, Clock, Trash2, MoreVertical, Settings, Users, MapPin, Play } from 'lucide-react';

const WIZARD_LABELS = ['Characters', 'Scene & Location', 'Frames', 'Audio', 'Production'];

const TABS = [
  { id: 'projects', label: 'Projects', icon: FolderOpen },
  { id: 'characters', label: 'Characters', icon: Users },
  { id: 'locations', label: 'Locations', icon: MapPin },
];
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';

export default function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTeam, setActiveTeam] = useState(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [keysConfigured, setKeysConfigured] = useState(false);
  useEffect(() => {
    api.get('/teams').then(r => { const t = (r.data || []).find(x => x.is_active); return t ? api.get(`/teams/${t.id}/api-keys/status`) : null; })
      .then(r => { if (r) setKeysConfigured(Object.values(r.data.configured_keys || {}).some(Boolean)); }).catch(() => {});
  }, []);
  const tab = TABS.some(t => t.id === searchParams.get('tab')) ? searchParams.get('tab') : 'projects';
  const setTab = (id) => setSearchParams(id === 'projects' ? {} : { tab: id }, { replace: true });

  const fetchProjects = async () => {
    try {
      const [projRes, teamsRes] = await Promise.all([
        api.get('/projects'),
        api.get('/teams'),
      ]);
      setProjects(projRes.data);
      setActiveTeam(teamsRes.data.find(t => t.is_active) || null);
    } catch (err) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
    const onWsChange = () => { setLoading(true); fetchProjects(); };
    window.addEventListener('workspace-changed', onWsChange);
    return () => window.removeEventListener('workspace-changed', onWsChange);
  }, []);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/projects/${id}`);
      setProjects(projects.filter(p => p.id !== id));
      toast.success('Project deleted');
    } catch (err) {
      toast.error('Failed to delete project');
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-[#060608]" data-testid="dashboard-page">
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-10">
        <div className="flex items-center justify-between mb-6 md:mb-10">
          <div>
            {activeTeam && (
              <div className="flex items-center gap-2 mb-1" data-testid="dashboard-workspace-name">
                <span className="w-6 h-6 rounded-md bg-[#FF4500]/15 text-[#FF4500] text-xs font-bold flex items-center justify-center">
                  {activeTeam.name.charAt(0).toUpperCase()}
                </span>
                <span className="text-sm font-medium text-[#8A8A93]">{activeTeam.name}</span>
              </div>
            )}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {TABS.find(t => t.id === tab).label}
            </h1>
            <p className="text-[#8A8A93] mt-1 md:mt-2 text-sm md:text-base">{tab === 'projects' ? 'Your video frame generation workspaces' : tab === 'characters' ? 'Every character across this workspace — click one to edit it or see where it appears' : 'Every location across this workspace — click one to edit it or see where it is used'}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="dashboard-workspace-settings-btn"
              onClick={() => navigate('/settings')}
              title="Workspace settings"
              className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] text-[#8A8A93] hover:text-white transition-colors text-sm"
            >
              <Settings className="w-4 h-4" />
              <span className="hidden md:inline">Settings</span>
            </button>
            <button
              data-testid="create-project-btn"
              onClick={() => navigate('/project/new')}
              className="ff-btn-primary flex items-center gap-2 text-sm md:text-base"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">New Project</span>
              <span className="sm:hidden">New</span>
            </button>
          </div>
        </div>

        <div className="flex gap-1 p-1 rounded-xl bg-[#121216] border border-white/[0.06] w-fit mb-6 md:mb-8" data-testid="workspace-tabs">
          {TABS.map(t => (
            <button key={t.id} data-testid={`workspace-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${tab === t.id ? 'bg-[#FF4500] text-white' : 'text-[#8A8A93] hover:text-white'}`}>
              <t.icon className="w-4 h-4" /> {t.label}{t.id === 'projects' && !loading ? ` (${projects.length})` : ''}
            </button>
          ))}
        </div>

        {tab === 'characters' && <WorkspaceCharactersTab />}
        {tab === 'locations' && <WorkspaceLocationsTab />}
        {tab === 'projects' && (loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3].map(i => (
              <div key={i} className="ff-card p-6 h-40 ff-pulse" />
            ))}
          </div>
        ) : projects.length === 0 ? (
          <GettingStarted title="Set up this workspace in three steps" subtitle="Everything here is shared with your teammates in this workspace."
            steps={[
              { label: 'Connect your AI provider keys', hint: 'Kling, Veo or Seedance for video · Gemini/OpenAI for images · ElevenLabs for voices. Test each key as you add it.', done: keysConfigured, action: { label: 'Team Settings', onClick: () => navigate('/settings') } },
              { label: 'Create your first project', hint: 'Give it a name, pick a visual style and aspect ratio.', done: false, action: { label: 'New Project', onClick: () => navigate('/project/new') } },
              { label: 'Import a script and generate your cast', hint: 'Paste or upload the script — characters and scenes are extracted automatically, then generate character images.', done: false },
            ]} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map(project => (
              <div
                key={project.id}
                className="ff-card group cursor-pointer"
                data-testid={`project-card-${project.id}`}
              >
                <div
                  className="p-6"
                  onClick={() => navigate(`/project/${project.id}`)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-10 h-10 rounded-lg bg-[#FF4500]/10 flex items-center justify-center">
                      <FolderOpen className="w-5 h-5 text-[#FF4500]" />
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          onClick={(e) => e.stopPropagation()}
                          className="p-1 rounded hover:bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`project-menu-${project.id}`}
                        >
                          <MoreVertical className="w-4 h-4 text-[#8A8A93]" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-[#1A1A20] border-white/10">
                        <DropdownMenuItem
                          onClick={(e) => { e.stopPropagation(); handleDelete(project.id); }}
                          className="text-red-400 focus:text-red-400 focus:bg-red-500/10 cursor-pointer"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <h3 className="text-lg font-semibold mb-1 text-white">{project.name}</h3>
                  <p className="text-sm text-[#8A8A93] line-clamp-2 mb-4">{project.description || 'No description'}</p>
                  <div className="flex items-center justify-between gap-2 text-xs text-[#55555C]">
                    <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{formatDate(project.updated_at)}</span>
                    {project.script_text && (project.current_scene_index > 0 || project.wizard_step > 0) && (
                      <button data-testid={`project-resume-${project.id}`} onClick={(e) => { e.stopPropagation(); navigate(`/project/${project.id}/production`); }}
                        className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-[#FF4500]/10 text-[#FF4500] border border-[#FF4500]/20 hover:bg-[#FF4500]/20 transition-colors">
                        <Play className="w-3 h-3" /> Continue · Scene {(project.current_scene_index || 0) + 1} · {WIZARD_LABELS[project.wizard_step || 0]}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </main>

    </div>
  );
}
